// src/hooks/useGroups.js
//
// Shared groups-data hook, extracted from GroupsGrid.jsx (July 2026) so the full Groups
// discovery page and the front-page GroupsColumn fetch the same four datasets the same way —
// one source of truth, per the pattern already used by useDirectoryListings/useGroupHome.
//
// Preserves GroupsGrid's exact behaviour, just relocated:
//   - Guests (no userId) skip the membership/owned queries; public discovery still runs.
//   - Suggested = public groups minus joined/owned, capped at 10.
//   - Two distinct click behaviours: selectGroup (direct, for Joined — already a member) vs
//     visitGroup (landing-page-first, for Owned/Discover/Suggested/Search).

import { useState, useEffect, useCallback } from 'react';
import { groupService } from '../data/dataService';
import { supabaseFetch } from '../lib/supabase';

export function useGroups({ currentUser, sessionReady = false, onGroupSelect, selectedArea = null } = {}) {
  const [joinedGroups, setJoinedGroups] = useState([]);
  const [ownedGroups, setOwnedGroups] = useState([]);
  const [suggestedGroups, setSuggestedGroups] = useState([]);
  const [discoverGroups, setDiscoverGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  const userId = currentUser?.id || null;

  const loadGroupsData = useCallback(async () => {
    setLoading(true);
    try {
      // Guest / signed-out: skip the membership and owned-groups queries entirely.
      // getUserGroups(null) returns a false-empty, and getOwnedGroups(null) would
      // query owner_id = null (orphaned/system groups). Public discovery still runs.
      // Searcher's context boundaries for P2 reverse-reach ranking: the single currently
      // selected Area only — NOT a union across every Area a user has saved. Ruled by the
      // Architect (docs/escalations/2026-09-08-p2-searcher-area-union-scope.md), grounded in
      // TRUE §920: persona switching is explicit and cross-persona context mixing is
      // architecturally prevented, so unioning all of a user's Areas would contaminate what
      // become separate personas' contexts once Personas ship. selectedArea is the MVP proxy
      // for "the active persona's context" until then — same object useDirectoryListings.js
      // (:120-125) and eventService.getLocalityEvents already key off for the equivalent
      // single-area read elsewhere in this codebase.
      //
      // Union of that ONE area's primary_boundary_id + boundary_ids (not a fallback chain to a
      // single id, since the RPC takes an array) — mirrors the migration's own per-area union
      // logic (20260908b §1a/1b) so a home boundary missing from its own boundary_ids array
      // still counts. No selected Area (guest, or logged-in with none saved) → [] —
      // getGroupsByReverseReach's documented graceful fallback (every discoverable group
      // returned at score 0, unranked-but-present).
      const boundaryIds = selectedArea
        ? [...new Set(
            [selectedArea.primary_boundary_id, ...(selectedArea.boundary_ids || [])]
              .filter(Boolean)
          )]
        : [];

      const [joined, owned, allPublic] = await Promise.all([
        userId ? groupService.getUserGroups(userId) : Promise.resolve([]),
        userId ? groupService.getOwnedGroups(userId) : Promise.resolve([]),
        groupService.getGroupsByReverseReach(boundaryIds)
      ]);

      setJoinedGroups(joined || []);
      setOwnedGroups(owned || []);

      const joinedIds = new Set((joined || []).map(g => g.id));
      const ownedIds = new Set((owned || []).map(g => g.id));
      const filteredSuggested = (allPublic || [])
        .filter(g => !joinedIds.has(g.id) && !ownedIds.has(g.id))
        .slice(0, 10);
      setSuggestedGroups(filteredSuggested);

      // Discover tab shows ALL public groups (not filtered by membership)
      setDiscoverGroups(allPublic || []);

    } catch (error) {
      console.error('❌ Error loading groups:', error);
      setJoinedGroups([]);
      setOwnedGroups([]);
      setSuggestedGroups([]);
      setDiscoverGroups([]);
    } finally {
      setLoading(false);
    }
  // Keyed on selectedArea?.id, not the selectedArea object itself — same convention
  // App.js's own HeaderButtonsFeed effect uses for the equivalent "did the selected Area
  // change" check, avoiding a reload on every re-render a new-but-equal object would cause.
  }, [userId, selectedArea?.id]);

  // Gating on sessionReady prevents the fetch from running against a null userId
  // mid-restore, which produced a false-empty "Groups I've Joined".
  useEffect(() => {
    if (!sessionReady) return;
    loadGroupsData();
  }, [sessionReady, loadGroupsData]);

  // Direct select — used for Joined groups (already a member, no landing check needed).
  const selectGroup = useCallback((group) => {
    onGroupSelect?.(group);
  }, [onGroupSelect]);

  // Landing-page-first visit — used for Owned/Discover/Suggested/Search. The Introduction page
  // is for someone deciding whether to join; an owner/admin/member goes straight in instead
  // (Michael's ruling). Union of both ownership signals, same reasoning as
  // getFederationEligibleApplicantGroups: owner_id is already on the card (no extra query),
  // community_members needs one cheap, existing, no-nested-join lookup.
  const visitGroup = useCallback(async (group) => {
    const isOwner = !!(userId && group.owner_id && group.owner_id === userId);
    if (isOwner) {
      selectGroup(group);
      return;
    }
    if (userId) {
      const { isMember } = await groupService.checkMembership(userId, group.id);
      if (isMember) {
        selectGroup(group);
        return;
      }
    }

    try {
      const { data } = await supabaseFetch.query('community_landing', {
        select: 'id',
        eq: { community_id: group.id, is_published: true },
        single: true
      });

      if (data) {
        window.dispatchEvent(new CustomEvent('ournet:navigate', {
          detail: { view: 'group-landing-view', group }
        }));
        return;
      }
    } catch (err) {
      // No landing page found or error - fall through to default behavior
    }

    selectGroup(group);
  }, [selectGroup, userId]);

  const joinGroup = useCallback(async (group, requireAuth, onResult) => {
    requireAuth('gate_body_join_group', async () => {
      const actualUserId = currentUser?.id;
      if (!actualUserId) {
        console.error('❌ No user ID available');
        return;
      }

      const result = await groupService.joinGroup(actualUserId, group.id);
      if (result?.success) {
        loadGroupsData();
      } else {
        console.error('❌ Failed to join group:', result?.message);
      }
      onResult?.(result);
    });
  }, [currentUser, loadGroupsData]);

  return {
    joinedGroups,
    ownedGroups,
    suggestedGroups,
    discoverGroups,
    loading,
    selectGroup,
    visitGroup,
    joinGroup,
    reload: loadGroupsData
  };
}

export default useGroups;
